import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@i9portal/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Alert } from '../../components/alert';
import { ErrorMessage } from '../../components/error-state';
import { Field, fieldA11y } from '../../components/field';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { applyApiErrorToForm } from '../../lib/forms';
import { useAuth } from '../../lib/auth/use-auth';

interface LocationState {
  from?: string;
  message?: string;
}

export function LoginPage() {
  useDocumentTitle('Entrar');
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [submitError, setSubmitError] = useState<unknown>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'authenticated') return <Navigate to={state.from ?? '/'} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login({ email: values.email, password: values.password });
      navigate(state.from && state.from !== '/login' ? state.from : '/', { replace: true });
    } catch (error) {
      if (!applyApiErrorToForm(error, setError)) setSubmitError(error);
    }
  });

  return (
    <>
      <h1 className="public__title">Entrar</h1>
      {state.message && <Alert tone="success">{state.message}</Alert>}
      <form className="stack" onSubmit={onSubmit} noValidate>
        <Field label="E-mail" htmlFor="login-email" error={errors.email?.message} required>
          <input
            type="email"
            className="input"
            autoComplete="username"
            autoFocus
            {...fieldA11y('login-email', errors.email?.message)}
            {...register('email')}
          />
        </Field>
        <Field label="Senha" htmlFor="login-password" error={errors.password?.message} required>
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            {...fieldA11y('login-password', errors.password?.message)}
            {...register('password')}
          />
        </Field>
        {submitError ? <ErrorMessage error={submitError} /> : null}
        <button type="submit" className="btn btn--primary btn--block" disabled={isSubmitting}>
          {isSubmitting ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
      <p className="public__links">
        <Link to="/esqueci-senha">Esqueci minha senha</Link>
      </p>
    </>
  );
}
